/**
 * Utility functions for date formatting across the application
 */

/**
 * Format date in chronological format (MM/DD/YYYY)
 * @param dateString - Date string to format
 * @returns Formatted date string or empty string if invalid
 */
export const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    let date: Date;
    
    // Handle DD-MM-YYYY format (e.g., "17-01-2025")
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const parts = dateString.split('-');
      if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        // DD-MM-YYYY format
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        date = new Date(year, month, day);
      } else {
        // Try default parsing for other formats
        date = new Date(dateString);
      }
    } else {
      // Try default parsing for other formats
      date = new Date(dateString);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }
    
    const month = date.getMonth() + 1; // getMonth() returns 0-11, so add 1
    const day = date.getDate();
    const year = date.getFullYear(); // Get full 4-digit year
    
    return `${day.toString().padStart(2, '0')}-${month.toString().padStart(2, '0')}-${year}`;
  } catch (error) {
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Format date and time in chronological format (MM/DD/YYYY HH:MM AM/PM)
 * @param dateString - Date string to format
 * @returns Formatted date and time string or empty string if invalid
 */
export const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    let date: Date;
    
    // Handle DD-MM-YYYY format (e.g., "17-01-2025")
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const parts = dateString.split('-');
      if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        // DD-MM-YYYY format
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        date = new Date(year, month, day);
      } else {
        // Try default parsing for other formats
        date = new Date(dateString);
      }
    } else {
      // Try default parsing for other formats
      date = new Date(dateString);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }
    
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    return dateString; // Return original string if parsing fails
  }
};

/**
 * Format date for Excel export (YYYY-MM-DD format)
 * @param dateString - Date string to format
 * @returns Formatted date string for Excel or empty string if invalid
 */
export const formatDateForExcel = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  
  try {
    let date: Date;
    
    // Handle DD-MM-YYYY format (e.g., "17-01-2025")
    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const parts = dateString.split('-');
      if (parts[0].length === 2 && parts[1].length === 2 && parts[2].length === 4) {
        // DD-MM-YYYY format
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1; // Month is 0-indexed
        const year = parseInt(parts[2], 10);
        date = new Date(year, month, day);
      } else {
        // Try default parsing for other formats
        date = new Date(dateString);
      }
    } else {
      // Try default parsing for other formats
      date = new Date(dateString);
    }
    
    // Check if the date is valid
    if (isNaN(date.getTime())) {
      return dateString; // Return original string if invalid
    }
    
    return date.toISOString().split('T')[0]; // YYYY-MM-DD format
  } catch (error) {
    return dateString; // Return original string if parsing fails
  }
};
